import type { ColDef, ICellRendererParams } from "ag-grid-community"
import type { AgGridReact } from "ag-grid-react"
import { useEffect, useMemo, useRef } from "react"
import { toast } from "react-toastify"

import ExpandableSearchField from "~/components/planning/ExpandableSearchField"
import {
  InspectorPeekCellButton,
  useOpenInspector,
  type HighlightSubscription,
} from "~/components/design-system/pages"
import { DataTableBaseV2 } from "~/components/reusable/DataTableV2"
import { AllocationStoreColumn, ALLOCATION_STORES_TABLE_ID } from "~/constants/allocationStoreTable"
import { useGridInspectorSelection } from "~/hooks/useGridInspectorSelection"
import { usePermission } from "~/hooks/usePermission"
import { clampEditable } from "~/utils/ag-utils"
import {
  MAX_DELIVERY_ADDRESS_LENGTH,
  MAX_SELLING_AREA,
  MAX_TRANSIT_DAYS,
  OUTLET_FORMAT,
  STORE_FORMATS,
  STORE_GRADES,
  STORE_SIZES,
  storePatchSchema,
} from "~/utils/allocationStores"
import { createNumberColumn, createStringColumn } from "~/utils/columnTemplates"
import { formatNumber } from "~/utils/formatting"
import { cn } from "~/utils/helpers"
import { DraftCommandbar } from "../../DraftCommandbar"
import {
  formatStoreValue,
  normalizeStoreText,
  type StoreEdits,
  type StoreValues,
} from "../drafts/storeDraftState"
import type { StoreField, StoreFormat, StoreGrade } from "~/utils/allocationStores"
import { matchesStoreSearch, STORE_SEARCH_OPTIONS, type StoreSearchRow } from "~/utils/storeSearch"
import { useStoreSearch } from "../../useStoreSearch"
import { useStoreSetupDraft } from "./useStoreSetupDraft"
import type { StoreSetupRow } from "./useStoreSetup"

type StoreGridRow = StoreSetupRow & { staged: StoreValues; edited: StoreEdits }

const EDITED_CELL = "bg-adjusted-bg font-medium text-adjusted-fg"
const NONE = ""

const OUT_OF_RANGE = "allocation-store-out-of-range"

/** A whole number the save accepts, null for an emptied cell, undefined (refused with a message) otherwise. */
const toWholeNumber = (
  value: unknown,
  field: "sellingArea" | "transitDays",
  max: number,
): number | null | undefined => {
  const text = normalizeStoreText(value)
  if (text === null) return null
  const parsed = storePatchSchema.shape[field].safeParse(Number(text))
  if (parsed.success && typeof parsed.data === "number") return parsed.data
  toast.error(`Enter a whole number from 0 to ${formatNumber(max)}.`, { toastId: OUT_OF_RANGE })
  return undefined
}

const toGrade = (value: unknown): StoreGrade | null | undefined => {
  const text = normalizeStoreText(value)
  if (text === null) return null
  const parsed = storePatchSchema.shape.grade.safeParse(text)
  return parsed.success ? parsed.data : undefined
}

const toFormat = (value: unknown): StoreFormat | undefined => {
  const parsed = storePatchSchema.shape.format.safeParse(value)
  return parsed.success ? parsed.data : undefined
}

const toSearchRow = (row: StoreSetupRow): StoreSearchRow => ({
  name: row.name,
  ids: [row.code],
  market: row.country,
  address: row.values.deliveryAddress,
})

type NameCellParams = ICellRendererParams<StoreGridRow> & {
  subscription: HighlightSubscription
  onInspect: (id: string) => void
}

const NameCell = ({ data, node, subscription, onInspect }: NameCellParams) =>
  data ? (
    <>
      <span className="label-base text-fg-base">{data.name}</span>
      <InspectorPeekCellButton
        rowId={data.id}
        subscription={subscription}
        onOpen={onInspect}
        onClose={keepRowSelected}
        rowNode={node}
      />
    </>
  ) : null

// Closing the inspector keeps the store selected, so reopening it lands on the same store.
const keepRowSelected = () => {}

/** Settings › Stores: one editable row per store; Inspect opens its timeline and setup. */
export default function SetupStores({ focusStoreId }: { focusStoreId?: string }) {
  const gridRef = useRef<AgGridReact<StoreGridRow> | null>(null)
  const draft = useStoreSetupDraft()
  const { allowed: canAdminister } = usePermission("administer")
  const { search, setSearch, searchBy, setSearchBy } = useStoreSearch(
    ALLOCATION_STORES_TABLE_ID,
    STORE_SEARCH_OPTIONS,
  )
  const openInspector = useOpenInspector()

  const { stage, ruleName, stagedValues, editsOf } = draft

  const rows = useMemo<StoreGridRow[]>(
    () =>
      draft.stores
        .filter((row) => matchesStoreSearch(toSearchRow(row), search, searchBy))
        .map((row) => ({ ...row, staged: stagedValues(row), edited: editsOf(row.id) ?? {} })),
    [draft.stores, stagedValues, editsOf, search, searchBy],
  )

  const { select, highlight, inspect, onSelectionChanged } = useGridInspectorSelection(
    gridRef,
    rows,
  )

  const focusedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!focusStoreId || focusedRef.current === focusStoreId) return
    if (!draft.stores.some((store) => store.id === focusStoreId)) return
    focusedRef.current = focusStoreId
    select(focusStoreId)
    openInspector()
    const node = gridRef.current?.api?.getRowNode(focusStoreId)
    if (node) gridRef.current?.api?.ensureNodeVisible(node, "middle")
  }, [focusStoreId, draft.stores, select, openInspector])

  const columnDefs = useMemo<ColDef<StoreGridRow>[]>(() => {
    const editable = clampEditable({ editable: true }, canAdminister)
    const field = <F extends StoreField>(
      key: F,
      parse: (value: unknown) => StoreValues[F] | undefined,
    ): Pick<
      ColDef<StoreGridRow>,
      "editable" | "valueGetter" | "valueSetter" | "valueFormatter" | "cellClass"
    > => ({
      editable,
      valueGetter: ({ data }) => data?.staged[key] ?? NONE,
      valueFormatter: ({ value }) => formatStoreValue(key, value),
      valueSetter: ({ data, newValue }) => {
        const value = parse(newValue)
        if (data && value !== undefined) stage(data, key, value)
        return false
      },
      cellClass: ({ data }) => (data && key in data.edited ? EDITED_CELL : undefined),
    })
    const selectEditor = (values: readonly string[]) => ({
      cellEditor: "agSelectCellEditor",
      cellEditorParams: { values },
    })

    return [
      createStringColumn<StoreGridRow>({
        colId: AllocationStoreColumn.Name,
        field: "name",
        minWidth: 240,
        pinned: "left",
        cellRenderer: NameCell,
        cellRendererParams: { subscription: highlight, onInspect: inspect },
      }),
      createStringColumn<StoreGridRow>({
        colId: AllocationStoreColumn.Code,
        field: "code",
        minWidth: 110,
        cellClass: "text-fg-muted",
      }),
      createStringColumn<StoreGridRow>({
        colId: AllocationStoreColumn.Grade,
        minWidth: 110,
        ...field("grade", toGrade),
        ...selectEditor([NONE, ...STORE_GRADES]),
        editable: (params) => editable !== false && params.data?.staged.format !== OUTLET_FORMAT,
      }),
      createStringColumn<StoreGridRow>({
        colId: AllocationStoreColumn.Format,
        minWidth: 130,
        ...field("format", toFormat),
        ...selectEditor(STORE_FORMATS.map((format) => format.value)),
      }),
      createStringColumn<StoreGridRow>({
        colId: AllocationStoreColumn.Size,
        minWidth: 110,
        ...field("storeSize", normalizeStoreText),
        ...selectEditor([NONE, ...STORE_SIZES]),
      }),
      createNumberColumn<StoreGridRow>({
        colId: AllocationStoreColumn.SellingArea,
        minWidth: 130,
        aggFunc: undefined,
        ...field("sellingArea", (value) => toWholeNumber(value, "sellingArea", MAX_SELLING_AREA)),
        cellEditor: "agNumberCellEditor",
        cellEditorParams: { min: 0, max: MAX_SELLING_AREA, precision: 0 },
      }),
      createNumberColumn<StoreGridRow>({
        colId: AllocationStoreColumn.TransitTime,
        minWidth: 140,
        aggFunc: undefined,
        ...field(
          "transitDays",
          (value) => toWholeNumber(value, "transitDays", MAX_TRANSIT_DAYS) ?? undefined,
        ),
        cellEditor: "agNumberCellEditor",
        cellEditorParams: { min: 0, max: MAX_TRANSIT_DAYS, precision: 0 },
      }),
      createStringColumn<StoreGridRow>({
        colId: AllocationStoreColumn.Rule,
        minWidth: 180,
        valueGetter: ({ data }) => data?.ruleId ?? null,
        valueFormatter: ({ value }) => (value ? ruleName(String(value)) : "No rule"),
        cellClass: "text-fg-muted",
      }),
      createStringColumn<StoreGridRow>({
        colId: AllocationStoreColumn.DeliveryAddress,
        minWidth: 240,
        ...field("deliveryAddress", normalizeStoreText),
        cellEditor: "agTextCellEditor",
        cellEditorParams: { maxLength: MAX_DELIVERY_ADDRESS_LENGTH },
      }),
      createStringColumn<StoreGridRow>({
        colId: AllocationStoreColumn.Country,
        field: "country",
        minWidth: 110,
        cellClass: "text-fg-muted",
      }),
    ]
  }, [canAdminister, ruleName, stage, highlight, inspect])

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="label-sm text-fg-subtle">
          {rows.length} of {draft.stores.length} stores
        </span>
        <div className="flex-1" />
        <ExpandableSearchField
          initialSearchText={search}
          onSearchTextChange={setSearch}
          placeholder="Search stores..."
          debounceTime={200}
          searchBy={searchBy}
          onSearchByChange={setSearchBy}
          searchByOptions={STORE_SEARCH_OPTIONS}
        />
      </div>
      <div className={cn("min-h-0 flex-1", draft.changes.length > 0 && "pb-16")}>
        <DataTableBaseV2<StoreGridRow>
          id={ALLOCATION_STORES_TABLE_ID}
          gridRef={gridRef}
          rowData={rows}
          rowIdField="id"
          columnDefs={columnDefs}
          defaultColumnDef={{ flex: 1, suppressHeaderMenuButton: true }}
          disableColumnSizingPersistence
          showPagination={false}
          rowSelection={{ mode: "singleRow", checkboxes: false, enableClickSelection: true }}
          onSelectionChanged={onSelectionChanged}
          loading={draft.isLoading}
          emptyMessage={
            search.trim() !== ""
              ? "No store matches that search."
              : "No stores yet. Stores come from your store data."
          }
        />
      </div>
      <DraftCommandbar
        requires="administer"
        changes={draft.changes}
        saving={draft.saving}
        onDiscard={draft.discard}
        onSave={draft.save}
      />
    </div>
  )
}
